import { memo, useCallback } from 'react';

import { MessageSquareIcon } from '@air/design-icons';

import { AutomationRunStatusDot } from '~/components/Automations/AutomationRunStatusDot';
import { useIsAutomationOwnedByViewer } from '~/components/Automations/hooks/useIsAutomationOwnedByViewer';
import {
  canOpenAutomationRunSession,
  getAutomationRunStatusLabel,
  shouldShowAutomationRunScheduleTrigger,
} from '~/components/Automations/utils/automationRunDisplay';
import { formatAutomationRunTimestamp } from '~/components/Automations/utils/automationSchedule';
import { WorkspaceAgentPanelMyTasksRowFrame } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowFrame';
import { WorkspaceAgentPanelMyTasksRowMetaContainer } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowMeta';
import { WorkspaceAgentPanelMyTasksRowPlainPreview } from '~/components/v2/components/workspace/workspace-agent-panel/components/tasks/WorkspaceAgentPanelMyTasksRowPreview';
import {
  getAutomationRunLastMessageContent,
  prepareWorkspaceAgentAutomationRunPreviewText,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentPanelAutomationDisplay';
import { type AutomationRun } from '~/hooks/queries/automations/usePrivateAutomationRunsList';

export type PrivateWorkspaceAgentPanelAutomationRunRowProps = {
  automationCreatedBy: string | undefined;
  now: Date;
  onOpenRun: (sessionId: string, run: AutomationRun) => void;
  run: AutomationRun;
  timeZone: string;
};

export const PrivateWorkspaceAgentPanelAutomationRunRow = memo(
  ({ automationCreatedBy, now, onOpenRun, run, timeZone }: PrivateWorkspaceAgentPanelAutomationRunRowProps) => {
    const { isOwnedByViewer } = useIsAutomationOwnedByViewer(automationCreatedBy);
    const preview = prepareWorkspaceAgentAutomationRunPreviewText(getAutomationRunLastMessageContent(run));
    const canOpenSession = isOwnedByViewer && canOpenAutomationRunSession(run);
    const statusLabel = getAutomationRunStatusLabel(run);
    const triggerLabel = shouldShowAutomationRunScheduleTrigger(run) ? 'Triggered by the schedule' : null;
    const title = formatAutomationRunTimestamp({
      at: run.startedAt,
      now,
      timeZone,
    });

    const handleClick = useCallback(() => {
      if (!canOpenSession || !run.sessionId) {
        return;
      }

      onOpenRun(run.sessionId, run);
    }, [canOpenSession, onOpenRun, run]);

    return (
      <li data-testid="WORKSPACE_AGENT_PANEL_AUTOMATION_RUN_ROW">
        <WorkspaceAgentPanelMyTasksRowFrame
          aria-disabled={!canOpenSession}
          icon={
            <span className="relative size-5 shrink-0 overflow-visible">
              <span className="flex size-5 items-center justify-center ads:text-icon-subtle text-gray-9">
                <MessageSquareIcon aria-hidden size={14} />
              </span>
            </span>
          }
          meta={
            <WorkspaceAgentPanelMyTasksRowMetaContainer>
              <span className="inline-flex shrink-0 items-center gap-1 font-semibold ads:text-primary text-gray-12">
                <AutomationRunStatusDot status={run.status} />
                {statusLabel}
              </span>
              {triggerLabel ? (
                <>
                  <span aria-hidden className="px-px font-medium ads:text-metadata text-gray-8">
                    ·
                  </span>
                  <span className="min-w-0 truncate">{triggerLabel}</span>
                </>
              ) : null}
            </WorkspaceAgentPanelMyTasksRowMetaContainer>
          }
          onClick={handleClick}
          overflowMenu={null}
          preview={preview ? <WorkspaceAgentPanelMyTasksRowPlainPreview text={preview} /> : null}
          showDrillInChevron={canOpenSession}
          title={title}
        />
      </li>
    );
  },
);

PrivateWorkspaceAgentPanelAutomationRunRow.displayName = 'PrivateWorkspaceAgentPanelAutomationRunRow';
